/**
 * Unidades de inventario (RES-17). Espejo de `services/inventory/shared/units.js` del backend.
 *
 * Catálogo cerrado: `un` (unidad), `g` / `kg` (masa), `ml` / `l` (volumen). Los datos viejos
 * pueden traer variantes (`gr`, `lt`, `unidad`): `normalizeUnit` las mapea a la canónica.
 */

export const CANONICAL_UNITS = ['un', 'g', 'kg', 'ml', 'l'];

const UNIT_FAMILY = { un: 'unit', g: 'mass', kg: 'mass', ml: 'volume', l: 'volume' };
const UNIT_FACTOR = { un: 1, g: 1, kg: 1000, ml: 1, l: 1000 };

const UNIT_ALIASES = {
  un: 'un',
  u: 'un',
  ud: 'un',
  uds: 'un',
  unidad: 'un',
  unidades: 'un',
  pcs: 'un',
  pieza: 'un',
  piezas: 'un',
  g: 'g',
  gr: 'g',
  grs: 'g',
  gramo: 'g',
  gramos: 'g',
  kg: 'kg',
  kgs: 'kg',
  kilo: 'kg',
  kilos: 'kg',
  kilogramo: 'kg',
  kilogramos: 'kg',
  ml: 'ml',
  cc: 'ml',
  mililitro: 'ml',
  mililitros: 'ml',
  l: 'l',
  lt: 'l',
  lts: 'l',
  litro: 'l',
  litros: 'l',
};

/**
 * Normaliza una unidad escrita de cualquier forma a su canónica.
 * @param {string} raw
 * @returns {string|null} canónica o null si no se reconoce
 */
export const normalizeUnit = (raw) => {
  if (raw === undefined || raw === null) return null;
  const key = String(raw).trim().toLowerCase().replace(/\.$/, '');
  if (!key) return null;
  return UNIT_ALIASES[key] || null;
};

/**
 * Familia de una unidad: 'unit' | 'mass' | 'volume' | null.
 * @param {string} unit
 * @returns {string|null}
 */
export const unitFamily = (unit) => UNIT_FAMILY[normalizeUnit(unit)] || null;

/**
 * Unidades canónicas de la misma familia que `unit` (para el selector de la receta).
 * Si la unidad no se reconoce devuelve todas.
 * @param {string} unit
 * @returns {string[]}
 */
export const familyUnits = (unit) => {
  const family = unitFamily(unit);
  return family ? CANONICAL_UNITS.filter((u) => UNIT_FAMILY[u] === family) : [...CANONICAL_UNITS];
};

/**
 * Convierte una cantidad entre unidades de la misma familia.
 * @param {number|string} quantity
 * @param {string} from
 * @param {string} to
 * @returns {number|null} cantidad convertida (6 decimales) o null si no se puede
 */
export const convertQuantity = (quantity, from, to) => {
  const qty = Number(quantity);
  const uFrom = normalizeUnit(from);
  const uTo = normalizeUnit(to);
  if (!Number.isFinite(qty) || !uFrom || !uTo || UNIT_FAMILY[uFrom] !== UNIT_FAMILY[uTo]) {
    return null;
  }
  return Math.round(((qty * UNIT_FACTOR[uFrom]) / UNIT_FACTOR[uTo]) * 1e6) / 1e6;
};
